import { Carousel } from '@liveauctioneers/hammer-ui-core/carousel';
import { CompleteAccountModalContext, ModalContext } from '@/redux/modules/modal';
import { EventContext } from '@/redux/modules/analytics';
import { getCarousels } from '@/redux/modules/itemCarousel/itemCarousel.selectors';
import { getItemSummaries } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getSalesHistoryItemIds } from '@/redux/modules/priceResults/priceResults.selectors';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { ItemSummaryWithRecommendation } from '@/types/item/ItemSummary';
import { LotRecommendationType } from '@/utils/analytics/item/trackLotRecommendationClicked.analytics';
import { SimilarSoldItemCard } from './components/SimilarSoldItemCard/SimilarSoldItemCard';
import { SingleCard } from '@liveauctioneers/hammer-ui-core/card/singleCard';
import { useAppSelector } from '@/redux/hooks';
import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import { useRequireLogin } from '@/components/ShouldLogIn/hooks/useRequireLogin';
import { YouMayAlsoLikeItemCard } from './components/YouMayAlsoLikeItemCard/YouMayAlsoLikeItemCard';

type Props = {
    itemId: number;
};

export const PriceResultsCarousels = ({ itemId }: Props) => {
    const { pathname } = useLocation();
    const verifyLogin = useRequireLogin();
    const salesHistoryItemIds = useAppSelector(getSalesHistoryItemIds);
    const similarSoldItemsCardSummaries = useAppSelector((state) => getItemSummaries(state, salesHistoryItemIds));

    // TODO only fetch the first carousel
    const carousels = useAppSelector((state) => getCarousels(state, itemId));

    const youMayAlsoLikeItemIds = carousels[1]?.itemIds ?? [];
    const youMayAlsoLikeCardSummaries = useAppSelector((state) => getItemSummaries(state, youMayAlsoLikeItemIds));
    const isLoggedIn = useAppSelector(isUserLoggedIn);

    const youMayAlsoLikeCards = useMemo(() => {
        return youMayAlsoLikeCardSummaries.map((card, index) => ({
            card: (
                <SingleCard<ItemSummaryWithRecommendation>
                    card={YouMayAlsoLikeItemCard}
                    className="h-[332px] w-[172px]"
                    data={{
                        ...card,
                        recommendationPosition: index + 1,
                        recommendationType: LotRecommendationType.YouMayAlsoLike,
                    }}
                    key={`you-may-also-like-card-${card.itemId}`}
                />
            ),
            key: `you-may-also-like-card-${card.itemId}`,
        }));
    }, [youMayAlsoLikeCardSummaries]);

    const similarSoldItemsCards = useMemo(() => {
        return similarSoldItemsCardSummaries.map((card, index) => ({
            card: (
                <SingleCard<ItemSummaryWithRecommendation>
                    card={SimilarSoldItemCard}
                    className="h-[332px] w-[172px]"
                    data={{
                        ...card,
                        recommendationPosition: index + 1,
                        recommendationType: LotRecommendationType.SimilarSaleHistory,
                    }}
                    key={`similar-sold-items-card-${card.itemId}`}
                />
            ),
            key: `similar-sold-items-card-${card.itemId}`,
        }));
    }, [similarSoldItemsCardSummaries]);

    const onUnlockAllPricesClicked = async () => {
        const modalContext: ModalContext = {
            eventContext: EventContext.seeSoldPrice,
            id: itemId,
            itemId,
            type: CompleteAccountModalContext.SeeSoldPrice,
        };
        await verifyLogin({ modalContext, requireLogin: true });
    };

    return (
        <div className="flex flex-col gap-2xl xsMax:gap-lg xsMax:px-sm">
            {youMayAlsoLikeCards.length > 0 && (
                <Carousel
                    cards={youMayAlsoLikeCards}
                    title="You May Also Like"
                />
            )}
            <Carousel
                action={
                    !isLoggedIn && {
                        hideIcon: true,
                        onClick: onUnlockAllPricesClicked,
                        // TODO add option to carousel to not have a path or prevent navigation
                        path: pathname,
                        text: 'Unlock All Prices',
                    }
                }
                cards={similarSoldItemsCards}
                title="Similar Sale History"
            />
        </div>
    );
};
